import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Loader2, FlaskConical, RefreshCw, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import Nav from "@/saas/components/Nav";
import Footer from "@/saas/components/Footer";
import SButton from "@/saas/components/SButton";
import TermsModal from "@/saas/components/TermsModal";
import { TERMS_VERSION } from "@/saas/legal/terms";
import { useI18n } from "@/saas/i18n";
import { PLANS, formatPrice } from "@/saas/config/plans";
import { useSaasAuth } from "@/saas/context/SaasAuthContext";

export default function Plans() {
  const { t } = useI18n();
  const { user, token } = useSaasAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(
    PLANS.find((p) => p.featured)?.id || PLANS[0].id
  );
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");
  const [stripeMode, setStripeMode] = useState(null); // 'live' | 'test' | 'unset' | null

  // Phase 8.1 — Legal guard: ToS acceptance gate
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  // Phase 10 — Marketing opt-in (KVKK; default OFF)
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/saas/config/stripe").then((res) => {
      if (alive) setStripeMode(res.data?.mode ?? null);
    }).catch(() => { /* silent — pill hidden */ });
    return () => { alive = false; };
  }, []);

  const onSelect = async (id) => {
    setSelected(id);
    setErr("");
    if (!token) {
      // Henüz giriş yapmadıysa kayıt ekranına gönder + plan'ı sakla
      sessionStorage.setItem("saas.pending_plan", id);
      navigate("/register");
      return;
    }
    if (!termsAccepted) {
      setErr("Devam etmek için Kullanım Şartlarını ve İptal Politikasını onaylamanız gerekiyor.");
      return;
    }
    setBusyId(id);
    try {
      const res = await api.post(
        "/saas/checkout",
        {
          plan_id:          id,
          origin_url:       window.location.origin,
          terms_accepted:   true,
          terms_version:    TERMS_VERSION,
          marketing_opt_in: marketingOptIn,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Stripe Checkout'a yönlendir
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setErr("Ödeme oturumu oluşturulamadı.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Ödeme oturumu oluşturulamadı.");
    } finally {
      setBusyId(null);
    }
  };

  // ToS engeli: oturum açmış kullanıcı için CTA aktivasyonu sözleşme onayına bağlı.
  // Anonim kullanıcı /register'a yönlendirildiğinden engellenmez.
  const canCheckout = !user || termsAccepted;

  return (
    <div className="min-h-screen bg-bg text-white">
      <Nav />

      {/* Stripe TEST MODE rozeti — sadece test/unset modlarda görünür, live modda otomatik gizlenir */}
      {stripeMode && stripeMode !== "live" && (
        <div
          className="fixed top-20 right-4 sm:right-6 z-40 pointer-events-none"
          data-testid="stripe-mode-badge"
          data-mode={stripeMode}
        >
          <div className={`pointer-events-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full border backdrop-blur-md shadow-xl ${
            stripeMode === "unset"
              ? "bg-red-500/15 border-red-500/40 text-red-300"
              : "bg-amber-500/15 border-amber-500/40 text-amber-300"
          }`}>
            <FlaskConical className="w-3.5 h-3.5" />
            <span className="text-[10px] font-mono font-bold uppercase tracking-widest">
              {stripeMode === "unset" ? "Stripe Yapılandırılmamış" : "Test Mode — Gerçek Ödeme Yok"}
            </span>
          </div>
        </div>
      )}

      <main className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/40 bg-gold/10 mb-5">
              <Sparkles className="w-3.5 h-3.5 text-gold" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-gold">{t("section.plans.kicker")}</span>
            </div>
            <h1 className="font-display font-extrabold text-5xl sm:text-6xl tracking-tighter leading-[0.95] mb-4">
              {t("plans.page.title")}
            </h1>
            <p className="text-lg text-white/60">{t("plans.page.subtitle")}</p>
          </div>

          {err && (
            <div className="mb-6 max-w-md mx-auto bg-red-500/10 border border-red-700/40 text-red-300 text-sm p-3 rounded-xl text-center" data-testid="plans-error">
              {err}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-5">
            {PLANS.map((p) => {
              const isSelected = p.id === selected;
              const isFeatured = !!p.featured;
              const isBusy = busyId === p.id;
              const buyDisabled = isBusy || (user && !termsAccepted);
              return (
                <div
                  key={p.id}
                  className={`relative p-8 rounded-2xl border transition-all duration-300 flex flex-col ${
                    isSelected
                      ? "border-gold bg-gradient-to-b from-gold/10 to-transparent shadow-[0_30px_100px_-32px_rgba(245,166,35,0.55)] -translate-y-1"
                      : isFeatured
                      ? "border-gold/30 bg-surface/70 hover:border-gold/60 hover:-translate-y-1"
                      : "border-white/10 bg-surface/60 hover:border-white/25 hover:-translate-y-1"
                  }`}
                >
                  {p.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-widest bg-gold text-black px-3 py-1 rounded-full shadow-lg">
                      {t(p.badge)}
                    </span>
                  )}

                  <div className="text-[11px] font-mono uppercase tracking-widest text-white/50 mb-3">
                    {t("plans.months", { n: p.months })}
                  </div>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="font-display text-6xl font-extrabold tracking-tighter leading-none">
                      {formatPrice(p.pricePerMonth)}
                    </span>
                    <span className="text-sm text-white/50">{t("plans.per_month")}</span>
                  </div>
                  <div className="text-xs text-white/50 mb-7">
                    {t("plans.billed_total", { amount: formatPrice(p.priceTotal) })}
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-white/80">
                        <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                        <span>{t(f)}</span>
                      </li>
                    ))}
                  </ul>

                  <SButton
                    variant={isSelected || isFeatured ? "primary" : "outline"}
                    className="w-full"
                    iconRight={isBusy ? Loader2 : ArrowRight}
                    onClick={() => onSelect(p.id)}
                    disabled={buyDisabled}
                    data-testid={`plans-select-${p.id}`}
                    aria-disabled={buyDisabled}
                    title={user && !termsAccepted ? "Önce Kullanım Şartlarını onaylayın" : undefined}
                  >
                    {isBusy ? "Yönlendiriliyor…" : (user ? "Ödeme Yap" : t("plans.select"))}
                  </SButton>
                </div>
              );
            })}
          </div>

          {/* ===== Phase 8.1 — Legal Guard ===== */}
          {/* Sözleşme onayı + otomatik yenilenme bilgilendirmesi sadece kayıtlı
              kullanıcılar için anlamlı (anonim kullanıcılar /register'a gider). */}
          {user && (
            <div
              className="mt-10 max-w-2xl mx-auto p-6 rounded-2xl border border-white/10 bg-surface/60 backdrop-blur-sm"
              data-testid="legal-guard"
            >
              <label
                htmlFor="terms-checkbox"
                className="flex items-start gap-3 cursor-pointer select-none group"
              >
                <input
                  id="terms-checkbox"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    if (e.target.checked) setErr("");
                  }}
                  className="mt-1 w-5 h-5 shrink-0 accent-gold cursor-pointer rounded border-white/30 bg-transparent"
                  data-testid="terms-checkbox"
                />
                <span className="text-sm text-white/85 leading-relaxed">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setTermsModalOpen(true); }}
                    className="underline decoration-gold/60 underline-offset-4 hover:text-gold hover:decoration-gold transition-colors"
                    data-testid="terms-open-link"
                  >
                    Kullanım Şartlarını ve İptal Politikasını
                  </button>
                  {" "}okudum, kabul ediyorum.
                  <span className="ml-1 text-[11px] font-mono text-white/40">
                    (sürüm {TERMS_VERSION})
                  </span>
                </span>
              </label>

              {/* Otomatik yenilenme bilgilendirmesi — ödeme butonu hemen üstünde */}
              <div
                className="mt-5 flex items-start gap-2.5 p-3.5 rounded-xl bg-gold/5 border border-gold/20"
                data-testid="auto-renew-disclaimer"
              >
                <RefreshCw className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                <p className="text-[13px] text-white/80 leading-relaxed">
                  <span className="font-semibold text-white">Otomatik Yenileme:</span>{" "}
                  Aboneliğiniz, siz iptal etmediğiniz sürece seçtiğiniz dönem sonunda
                  otomatik olarak yenilenir.
                </p>
              </div>

              {/* Phase 10 — Marketing opt-in checkbox */}
              <label
                htmlFor="plans-marketing"
                className="mt-3 flex items-start gap-3 cursor-pointer select-none px-1"
                data-testid="plans-marketing-row"
              >
                <input
                  id="plans-marketing"
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-gold cursor-pointer"
                  data-testid="plans-marketing-checkbox"
                />
                <span className="text-[13px] text-white/70 leading-relaxed">
                  Yeni içerikler ve özel indirimlerden e-posta ile haberdar olmak istiyorum.
                  <span className="ml-1 text-[10px] font-mono text-white/35 uppercase tracking-widest">opsiyonel · KVKK uyumlu</span>
                </span>
              </label>

              <div className="mt-3 flex items-center gap-2 text-[11px] text-white/45 font-mono">
                <ShieldCheck className="w-3.5 h-3.5 text-gold/60" />
                <span>Onayınız tarih, sürüm ve IP bilgisiyle güvenli şekilde kayıt altına alınır.</span>
              </div>
            </div>
          )}
        </div>
      </main>

      <TermsModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        onAccept={() => setTermsAccepted(true)}
      />

      <Footer />
    </div>
  );
}
